<template>
  <div class="mb-3.5" :class="msg.role === 'user' ? 'text-right' : ''">
    <!-- User bubble -->
    <div
      v-if="msg.role === 'user'"
      class="inline-block max-w-xs bg-gradient-to-br from-teal-600 to-purple-500 text-white px-3.5 py-2.5 rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br text-sm leading-relaxed"
    >
      {{ msg.content }}
    </div>

    <!-- Assistant bubble -->
    <div
      v-else
      class="inline-block w-full text-left bg-gray-50 border border-gray-100 px-4 py-3.5 rounded-tr-2xl rounded-br-2xl rounded-bl-2xl rounded-tl text-sm"
    >
      <!-- Loading -->
      <div v-if="msg.loading" class="flex gap-1.5 py-1.5 items-center">
        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
      </div>

      <template v-else>
        <!-- Title -->
        <p
          v-if="msg.parsed?.title"
          class="m-0 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-1.5"
        >
          {{ msg.parsed.title }}
        </p>

        <!-- Explanation -->
        <p
          v-if="msg.parsed?.explanation"
          class="m-0 mb-3 text-gray-700 leading-relaxed text-sm whitespace-pre-wrap"
        >
          {{ msg.parsed.explanation }}
        </p>
        <p v-else-if="!msg.parsed" class="m-0 text-gray-700 text-sm leading-relaxed">
          {{ msg.content }}
        </p>

        <!-- ── KPI Card ─────────────────────────────────────────────────── -->
        <template v-if="msg.parsed?.type === 'kpi_card' && msg.parsed.data?.kpis?.length">
          <div class="grid grid-cols-3 gap-2 mb-2">
            <div
              v-for="kpi in msg.parsed.data.kpis"
              :key="kpi.label"
              class="bg-white border border-gray-100 rounded-xl p-2.5 text-center transition-shadow hover:shadow-md min-w-0 overflow-hidden"
              style="border-top: 3px solid #6366f1"
            >
              <span class="kpi-value block text-sm font-bold text-indigo-500 leading-snug truncate">
                {{ formatKpiValue(kpi.value, kpi.unit) }}
              </span>
              <span class="block text-xs text-gray-400 mt-1 font-medium uppercase tracking-wide truncate leading-tight">
                {{ kpi.label }}
              </span>
              <span
                v-if="kpi.change_direction && kpi.change_direction !== 'neutral' && kpi.change_percent != null"
                :class="kpi.change_direction === 'up' ? 'text-emerald-600' : 'text-red-500'"
                class="block text-xs font-semibold mt-0.5"
              >
                {{ kpi.change_direction === 'up' ? '↑' : '↓' }}
                {{ Math.abs(kpi.change_percent).toFixed(1) }}%
              </span>
            </div>
          </div>
        </template>

        <!-- ── Chart ───────────────────────────────────────────────────── -->
        <div
          v-if="msg.parsed?.type === 'chart' && msg.parsed.data?.length"
          class="bg-white border border-gray-100 rounded-xl overflow-hidden my-2"
        >
          <ai-chart :data="msg.parsed.data" :visualization="msg.parsed.visualization" />
        </div>

        <!-- ── Table ───────────────────────────────────────────────────── -->
        <div
          v-if="msg.parsed?.type === 'table' && msg.parsed.data?.rows?.length"
          class="bg-white border border-gray-100 rounded-xl overflow-hidden my-2"
        >
          <ai-table :data="msg.parsed.data" />
        </div>

        <!-- ── Insights ────────────────────────────────────────────────── -->
        <div v-if="msg.parsed?.insights?.length" class="mt-3 space-y-1.5">
          <div
            v-for="(insight, i) in msg.parsed.insights"
            :key="i"
            :class="insightClasses(insight)"
            :style="insightBorderStyle(insight)"
            class="rounded-lg px-3 py-2 text-xs flex items-start gap-1.5 leading-relaxed"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="flex-shrink-0 mt-px"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span v-if="typeof insight === 'string'">{{ insight }}</span>
            <span v-else>
              <span class="font-semibold block">{{ insight.action }}</span>
              {{ insight.description }}
            </span>
          </div>
        </div>

        <!-- ── Follow-up suggestions ───────────────────────────────────── -->
        <div v-if="msg.parsed?.follow_up_suggestions?.length" class="mt-3 flex flex-wrap gap-2">
          <button
            v-for="(s, i) in msg.parsed.follow_up_suggestions"
            :key="i"
            @click="$emit('send-message', s)"
            class="bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1 text-xs cursor-pointer text-gray-600 hover:bg-gray-200 transition-colors"
          >
            {{ s }}
          </button>
        </div>

        <!-- ── Suggest summary ─────────────────────────────────────────── -->
        <div
          v-if="msg.parsed?.suggest_summary"
          class="mt-3 bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2.5"
        >
          <p class="text-xs font-semibold text-indigo-600 m-0 mb-1.5 flex items-center gap-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            {{ $t("ai.widget.summaryTitle") }}
          </p>
          <p class="text-xs text-indigo-800 m-0 mb-2 leading-relaxed">
            {{ msg.parsed.suggest_summary }}
          </p>
          <button
            @click="$emit('generate-summary', msg.parsed.suggest_summary)"
            class="inline-flex items-center gap-1.5 bg-indigo-100 border border-indigo-200 rounded-full px-3 py-1 text-xs cursor-pointer text-indigo-700 hover:bg-indigo-200 transition-colors font-medium"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            {{ $t("ai.widget.suggestSummary") }}
          </button>
        </div>

        <!-- ── Suggest report ──────────────────────────────────────────── -->
        <div v-if="msg.parsed?.suggest_report" class="mt-3 flex flex-wrap gap-2">
          <button
            @click="$emit('open-report-form', msg)"
            class="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-xs cursor-pointer text-emerald-700 hover:bg-emerald-100 transition-colors font-medium"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{ $t("ai.widget.suggestReport") }}
          </button>
        </div>

        <!-- ── Limit reached ──────────────────────────────────────────── -->
        <div
          v-if="msg.parsed?.type === 'limit_reached'"
          class="mt-3 flex flex-wrap gap-2"
        >
          <button
            @click="$emit('new-conversation')"
            class="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 rounded-full px-3 py-1 text-xs cursor-pointer text-indigo-700 hover:bg-indigo-100 transition-colors font-medium"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {{ $t("ai.widget.newConversation") }}
          </button>
          <button
            @click="$emit('generate-summary', $t('ai.widget.limitReachedSummaryPrompt'))"
            class="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-xs cursor-pointer text-emerald-700 hover:bg-emerald-100 transition-colors font-medium"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            {{ $t("ai.widget.suggestSummary") }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import AiChart from "./AiChart.vue";
import AiTable from "./AiTable.vue";

export default {
  name: "AiChatMessage",
  components: { AiChart, AiTable },

  props: {
    msg: { type: Object, required: true },
    idx: { type: Number, required: true },
  },

  emits: ["send-message", "generate-summary", "open-report-form", "new-conversation"],

  methods: {
    insightClasses(insight) {
      const severity = typeof insight === "object" ? insight.severity : null;
      if (severity === "high") return "bg-red-50 border border-red-200 text-red-900";
      if (severity === "medium") return "bg-orange-50 border border-orange-200 text-orange-900";
      if (severity === "low") return "bg-yellow-50 border border-yellow-200 text-yellow-900";
      return "bg-orange-50 border border-orange-200 text-orange-900";
    },

    insightBorderStyle(insight) {
      const severity = typeof insight === "object" ? insight.severity : null;
      if (severity === "high") return "border-left: 3px solid #ef4444";
      if (severity === "medium") return "border-left: 3px solid #f59e0b";
      if (severity === "low") return "border-left: 3px solid #eab308";
      return "border-left: 3px solid #f59e0b";
    },

    formatKpiValue(value, unit) {
      if (value === null || value === undefined) return "—";
      let formatted;
      if (typeof value === "number") {
        if (unit === "%") {
          formatted = `${value % 1 === 0 ? value : value.toFixed(1)}%`;
        } else if (unit === "s") {
          const m = Math.floor(value / 60);
          const s = Math.round(value % 60);
          formatted = m > 0 ? `${m}m ${s}s` : `${s}s`;
        } else if (unit === "min") {
          formatted = `${value % 1 === 0 ? value : value.toFixed(1)}m`;
        } else {
          formatted = value >= 1000 ? value.toLocaleString() : value % 1 === 0 ? String(value) : value.toFixed(1);
          if (unit) formatted += ` ${unit}`;
        }
      } else {
        formatted = String(value);
        if (unit && unit !== "%") formatted += ` ${unit}`;
      }
      return formatted;
    },
  },
};
</script>

<style scoped>
.kpi-value {
  font-variant-numeric: tabular-nums;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: ai-bounce 1.4s infinite both;
}
.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
@keyframes ai-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
  40%            { transform: scale(1); opacity: 1; }
}
</style>